import type { ColDef, ColGroupDef, ValueGetterParams } from 'ag-grid-enterprise';
import { IPeriodType, type ISubFundPnlQuery } from '../../../../../generated/graphql.tsx';
import { createColumnToolDef, getRowDataForRowGroupColumn } from '../../../../technical/grids/agGrid.utils.tsx';
import { getAssetName } from '../../../../market/asset/AssetService.tsx';
import type { CellClassParams, ICellRendererParams } from 'ag-grid-community';
import { Suspense, useMemo, type ReactElement } from 'react';
import AssetLabel from '../../../../market/asset/AssetLabel.tsx';
import { IconVariant } from '../../../../market/asset/cryptocurrencies/CryptocurrenciesData.tsx';
import isNil from 'lodash/fp/isNil';
import GAgGridPresets from '../../../../technical/grids/GAgGridPresets.tsx';
import { defaultPresets } from './subFundPnlGridPresets.ts';
import { upperFirst } from 'lodash/fp';
import Loader from 'components/technical/Loader/Loader.tsx';

type Query = ISubFundPnlQuery['portfolio']['subFundsTimeWeightedReturns']['subFunds'];
type PeriodRow = Query[number]['usdReturns']['summaries'][number];
type RowData = {
  subFund: Query[number]['subFund'];
  usdReturns: Partial<Record<IPeriodType, PeriodRow>>;
  referenceAssetReturns: Partial<Record<IPeriodType, PeriodRow>>;
};

const MIN_TABLE_HEIGHT = 400;
const MAX_TABLE_HEIGHT = 800; // Maximum table height
const ROW_HEIGHT = 55; // Estimated row height

enum SubFundAsset {
  Usd = 'USD',
  Reference = 'REFERENCE',
}

const periodName: Record<IPeriodType, string> = {
  [IPeriodType.All]: 'Whole period',
  [IPeriodType.Mtd]: 'MTD',
  [IPeriodType.Qtd]: 'QTD',
  [IPeriodType.Ytd]: 'YTD',
  [IPeriodType.Wtd]: 'WTD',
  [IPeriodType.D_7]: 'Last 7 days',
  [IPeriodType.D_30]: 'Last 30 days',
  [IPeriodType.D_90]: 'Last 90 days',
};

const cashColumnType = ['numericColumn', 'cashColumn'];
const percentageColumnType = ['numericColumn', 'percentageColumn'];
const numericColumnType = ['numericColumn', 'extendedNumericColumn'];
const dayTypes = ['all', 'negative', 'positive'] as const;

const extractUniqueQSMetrics = (data: Query): Record<IPeriodType, Set<string>> => {
  const metricMap: Record<IPeriodType, Set<string>> = {} as Record<IPeriodType, Set<string>>;

  for (const subFund of data) {
    for (const period of Object.keys(periodName)) {
      const usdMetrics = subFund.usdReturns.summaries.flatMap((summary) => summary.qsMetrics) || [];
      const referenceMetrics = subFund.referenceAssetReturns.summaries.flatMap((summary) => summary.qsMetrics) || [];

      if (!metricMap[period as IPeriodType]) {
        metricMap[period as IPeriodType] = new Set();
      }

      for (const metric of usdMetrics) {
        metricMap[period as IPeriodType].add(metric.displayName);
      }
      for (const metric of referenceMetrics) {
        metricMap[period as IPeriodType].add(metric.displayName);
      }
    }
  }

  return metricMap;
};

const createAssetsColumns = ({
  periodType,
  metricsForPeriod,
}: {
  periodType: IPeriodType;
  metricsForPeriod: Set<string>;
}): (ColDef<RowData> | ColGroupDef<RowData>)[] => {
  const getPeriodReturnStats = (
    asset: SubFundAsset,
    params: ICellRendererParams<RowData> | ValueGetterParams<RowData>
  ): PeriodRow | undefined => {
    if (!params.data) {
      return undefined;
    }

    const assetField = asset === SubFundAsset.Usd ? 'usdReturns' : 'referenceAssetReturns';
    return params.data[assetField][periodType as IPeriodType];
  };

  const returnColumns: ColDef<RowData>[] = [];
  const columns: (ColDef<RowData> | ColGroupDef<RowData>)[] = [
    {
      headerName: 'Return',
      groupId: 'return',
      marryChildren: true,
      children: returnColumns,
    },
  ];

  const positiveNegativeCellStyle = (params: CellClassParams<RowData, number>) => {
    if (isNil(params.value)) {
      return null;
    }

    return {
      color: params.value < 0 ? 'var(--joy-palette-danger-plainColor)' : 'var(--joy-palette-success-plainColor)',
    };
  };

  const unitTypes = ['absolute', 'percentage'] as const;
  for (const unitType of unitTypes) {
    for (const asset of [SubFundAsset.Usd, SubFundAsset.Reference]) {
      returnColumns.push({
        colId: `${asset}_cumm_ret_${unitType}`,
        headerName: ['P&L', asset === SubFundAsset.Usd ? '($)' : '', unitType === 'percentage' ? '[%]' : '']
          .filter((part) => !!part)
          .join(' '),
        cellStyle: positiveNegativeCellStyle,
        type:
          unitType === 'percentage'
            ? percentageColumnType
            : asset === SubFundAsset.Usd
              ? cashColumnType
              : numericColumnType,
        valueGetter: (params): number | undefined => {
          return getPeriodReturnStats(asset, params)?.cumulativeReturns[unitType];
        },
      });
    }
  }

  const retMetrics = ['dayCount', 'medianReturn', 'meanReturn', 'bestDay', 'worstDay'] as const;
  for (const retMetric of retMetrics) {
    const groupColumns: ColDef<RowData>[] = [];
    columns.push({
      headerName: upperFirst(retMetric.replaceAll(/([A-Z])/g, ' $1').toLowerCase()).replaceAll(/s$/g, ''),
      groupId: retMetric,
      marryChildren: true,
      children: groupColumns,
    });

    for (const dayType of dayTypes) {
      for (const unitType of ['absolute', 'percentage'] as const) {
        if (retMetric === 'dayCount' && unitType === 'percentage' && dayType === 'all') {
          continue;
        }

        for (const asset of [SubFundAsset.Usd, SubFundAsset.Reference]) {
          groupColumns.push({
            colId: `${asset}_${periodType}_${dayType}_${retMetric}_${unitType}`,
            headerName: upperFirst(
              [dayType, asset === SubFundAsset.Usd ? '($)' : '', unitType === 'percentage' ? '[%]' : '']
                .filter((part) => !!part)
                .join(' ')
            ),
            cellStyle: retMetric === 'dayCount' ? () => null : positiveNegativeCellStyle,
            type:
              unitType === 'percentage'
                ? percentageColumnType
                : retMetric === 'dayCount'
                  ? numericColumnType
                  : asset === SubFundAsset.Usd
                    ? cashColumnType
                    : numericColumnType,
            enableValue: unitType === 'absolute',
            valueGetter: (params: ValueGetterParams<RowData>): number | undefined | null => {
              const returnSummary = getPeriodReturnStats(asset, params);
              if (isNil(returnSummary)) {
                return undefined;
              }

              const dayStats = returnSummary.statistics[dayType];
              if (isNil(dayStats)) {
                return undefined;
              }

              return dayStats[retMetric][unitType];
            },
          });
        }
      }
    }
  }
  // Dynamically create columns for each unique QS Metric
  for (const metricName of metricsForPeriod) {
    for (const asset of [SubFundAsset.Usd, SubFundAsset.Reference]) {
      returnColumns.push({
        colId: `${asset}_${periodType}_${metricName.replace(/\s+/g, '_').toLowerCase()}`,
        headerName: `${metricName} (${asset})`,
        cellStyle: positiveNegativeCellStyle,
        type: numericColumnType,
        valueGetter: (params: ValueGetterParams<RowData>) => {
          const stats =
            asset === SubFundAsset.Usd
              ? params.data?.usdReturns[periodType]?.qsMetrics
              : params.data?.referenceAssetReturns[periodType]?.qsMetrics;

          const metric = stats?.find((m) => m.displayName === metricName);
          return metric ? metric.value : undefined;
        },
      });
    }
  }

  // Return on Risk Capital column - direct child of period group
  columns.push({
    colId: `${periodType}_return_on_risk_capital`,
    headerName: 'Return on Risk Capital [%]',
    cellStyle: positiveNegativeCellStyle,
    type: percentageColumnType,
    valueGetter: (params: ValueGetterParams<RowData>): number | null | undefined => {
      return getPeriodReturnStats(SubFundAsset.Reference, params)?.returnOnRiskCapital;
    },
  } as ColDef<RowData>);

  return columns;
};

const createPeriodBasedColumns = (data: Query): (ColDef<RowData> | ColGroupDef<RowData>)[] => {
  const uniqueMetrics = extractUniqueQSMetrics(data);

  return Object.entries(periodName).flatMap(([periodType, name]): ColDef<RowData>[] => {
    const metricsForPeriod = uniqueMetrics[periodType as IPeriodType] || new Set();

    const columns = createAssetsColumns({
      periodType: periodType as IPeriodType,
      metricsForPeriod,
    });

    const resultColumns: ColGroupDef<RowData>[] = [];
    resultColumns.push({
      headerName: name,
      groupId: periodType,
      marryChildren: true,
      children: [...columns],
    });

    return resultColumns;
  });
};

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const createPeriodToSummary = (
  assetValues: Query[number]['referenceAssetReturns']['summaries']
): Partial<Record<IPeriodType, PeriodRow>> => {
  return Object.fromEntries(assetValues.map((summ) => [summ.periodType, summ]));
};

const SubFundPnlGrid = ({
  data,
  fullHeight,
  presetKey,
}: {
  data: Query;
  fullHeight?: boolean;
  presetKey: string;
}) => {
  const rows = data.map((subFund) => {
    return {
      subFund: subFund.subFund,
      usdReturns: createPeriodToSummary(subFund.usdReturns.summaries),
      referenceAssetReturns: createPeriodToSummary(subFund.referenceAssetReturns.summaries),
    };
  });

  const columnDefs: (ColDef<RowData> | ColGroupDef<RowData>)[] = [
    {
      headerName: 'Sub-fund',
      type: 'textColumn',
      valueGetter: (params): string | undefined => {
        if (!params.data) {
          return undefined;
        }

        return params.data.subFund.name;
      },
    },
    {
      headerName: 'Valuation currency',
      type: 'textColumn',
      colId: 'valuationAsset',
      valueGetter: (params): string | undefined => {
        if (!params.data) {
          return undefined;
        }

        return getAssetName(params.data.subFund.referenceAsset);
      },
      cellRenderer: (params: ICellRendererParams<RowData>): ReactElement | undefined => {
        const rowData = getRowDataForRowGroupColumn(params);
        if (!rowData) {
          return undefined;
        }
        const asset = rowData.subFund.referenceAsset;
        return asset ? <AssetLabel asset={asset} wrap={false} size={IconVariant.MEDIUM} plain /> : undefined;
      },
    },
    ...createPeriodBasedColumns(data), // Dynamically pass data for metrics extraction
  ];

  // Calculate dynamic height based on the number of rows
  const dynamicHeight = useMemo(() => {
    const calculatedHeight = rows.length * ROW_HEIGHT;
    const minHeight = calculatedHeight > MIN_TABLE_HEIGHT ? calculatedHeight : MIN_TABLE_HEIGHT;
    return Math.min(minHeight, MAX_TABLE_HEIGHT);
  }, [rows.length]);

  return (
    <Suspense fallback={<Loader />}>
      <GAgGridPresets<RowData>
        presetSettingsKey={presetKey}
        defaultPresets={defaultPresets}
        rowData={rows}
        height={fullHeight ? '100%' : `${dynamicHeight}px`}
        groupDefaultExpanded={-1}
        sideBar={{
          toolPanels: [
            createColumnToolDef({
              suppressPivotMode: true,
            }),
            'filters',
          ],
        }}
        enableCharts
        cellSelection
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
      />
    </Suspense>
  );
};

export default SubFundPnlGrid;
